import { useState, useEffect, useCallback } from 'react';
import { useStore } from '@/utils/store';
import { fetchExchangeRate } from '@/utils/exchange-api';

const STALE_TTL = 4 * 60 * 60 * 1000; // 4 hours

export function useExchangeRate(from = 'USD', to = 'CLP') {
  const key = `${from}_${to}`;
  const exchangeRates = useStore((s) => s.exchangeRates);
  const setExchangeRate = useStore((s) => s.setExchangeRate);
  const setManualExchangeRate = useStore((s) => s.setManualExchangeRate);
  const clearManualExchangeRate = useStore((s) => s.clearManualExchangeRate);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const manualRate = exchangeRates.manualOverrides[key];
  const apiRate = exchangeRates.rates[key];
  const rate = manualRate ?? apiRate ?? null;
  const lastFetchedAt = exchangeRates.lastFetchedAt;
  const isStale = !lastFetchedAt || Date.now() - lastFetchedAt > STALE_TTL;

  const refresh = useCallback(async () => {
    if (from === to) return;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchExchangeRate(from, to);
      setExchangeRate(from, to, result.rate);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [from, to, setExchangeRate]);

  useEffect(() => {
    if (from === to) return;
    if (!apiRate || isStale) {
      refresh();
    }
  }, [from, to, apiRate, isStale, refresh]);

  const setManualRate = useCallback(
    (r) => setManualExchangeRate(from, to, r),
    [from, to, setManualExchangeRate]
  );

  const clearManualRate = useCallback(
    () => clearManualExchangeRate(from, to),
    [from, to, clearManualExchangeRate]
  );

  return { rate, loading, error, refresh, setManualRate, clearManualRate, isManual: manualRate != null };
}
